// GENERATED FILE — do not edit by hand.
// Source: backend/src/services/vmix/preset.js
// Regenerate: cd backend && node scripts/sync-vmix-libs.mjs
//
// The backend and frontend deploy separately, so the browser editor needs its
// own copy of the parser. vmix-libs.test.js fails the build if this drifts.

// Read and rewrite the <Scripting> block of a vMix preset (.vmix) file.
//
// CANONICAL COPY. The frontend gets a generated duplicate at
// frontend/src/lib/vmixPreset.js — the two packages deploy separately (Railway
// and Vercel) so neither can import the other at runtime. Edit this file and run
// `cd backend && node scripts/sync-vmix-libs.mjs`; vmix-libs.test.js fails the
// build if the copies drift.
//
// A preset is a ~1MB XML document describing every input, mixer, overlay and
// output in a production. We only ever touch the scripts, so this deliberately
// does NOT round-trip the document through DOMParser/XMLSerializer: doing so
// re-encodes the whole file (attribute quoting, self-closing tags, the many
// nested XML-in-attribute payloads such as Positions="&lt;?xml ...") and any
// difference there is a production risk for a file the operator will load back
// into a live mixer.
//
// Instead we locate the <Scripting>…</Scripting> byte range, parse only that,
// and splice a regenerated block back in. Every other byte of the file the
// operator downloads is identical to the one they dropped in.
//
// That byte range is also the only part Mission Control ever stores. A preset
// carries streaming destinations and stream keys outside <Scripting>; keeping
// the stored slice narrow is what stops those reaching the database at all.

const SCRIPTING_OPEN = '<Scripting>';
const SCRIPTING_CLOSE = '</Scripting>';

// vMix writes the file with CRLF line endings. We normalise to LF for editing
// (what a <textarea> gives us) and convert back on the way out.
const CRLF = '\r\n';

function decodeXml(s) {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#x9;/gi, '\t')
    .replace(/&#xD;/gi, '\r')
    .replace(/&#xA;/gi, '\n')
    .replace(/&#(\d+);/g, (_, d) => String.fromCharCode(Number(d)))
    .replace(/&amp;/g, '&');   // last, so "&amp;lt;" decodes to "&lt;" not "<"
}

// vMix escapes only these three in element text — quotes and apostrophes are
// left bare inside <Code>, which matters because script code is full of them.
function encodeXml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

let nextScriptId = 1;
export function newScriptId() {
  return `script-${nextScriptId++}`;
}

/**
 * Parse a preset's scripts.
 *
 * @returns {{
 *   text: string,            // the original file, untouched
 *   start: number,           // byte offset of '<Scripting>'
 *   end: number,             // byte offset just past '</Scripting>'
 *   arrayOpenTag: string,    // '<ArrayOfScript xmlns:xsd="…">' verbatim
 *   scripts: Array<{id, name, code}>,
 * }}
 * @throws if the file has no <Scripting> block at all.
 */
export function parsePreset(text) {
  const start = text.indexOf(SCRIPTING_OPEN);

  // A preset saved with scripting untouched can carry a self-closing element.
  if (start === -1) {
    const selfClosing = /<Scripting\s*\/>/.exec(text);
    if (selfClosing) {
      return {
        text,
        start: selfClosing.index,
        end: selfClosing.index + selfClosing[0].length,
        arrayOpenTag: '<ArrayOfScript xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">',
        scripts: [],
      };
    }
    throw new Error("This file has no <Scripting> section — it doesn't look like a vMix preset.");
  }

  const closeAt = text.indexOf(SCRIPTING_CLOSE, start);
  if (closeAt === -1) throw new Error('Malformed preset: <Scripting> is never closed.');
  const end = closeAt + SCRIPTING_CLOSE.length;

  const region = text.slice(start, end);

  // Whitespace already sitting before <Scripting> on its line. Everything we
  // emit is indented relative to that, so the block lands back at the depth
  // vMix wrote it at.
  const lineStart = text.lastIndexOf('\n', start) + 1;
  const baseIndent = /^[ \t]*$/.test(text.slice(lineStart, start)) ? text.slice(lineStart, start) : '';

  const arrayOpen = /<ArrayOfScript\b[^>]*>/.exec(region);
  const arrayOpenTag = arrayOpen
    ? arrayOpen[0]
    : '<ArrayOfScript xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">';

  const scripts = [];
  // <Code> content cannot itself contain a literal '</Script>' — XML requires
  // the '<' to be escaped — so a non-greedy match is safe here.
  const scriptRe = /<Script>([\s\S]*?)<\/Script>/g;
  let m;
  while ((m = scriptRe.exec(region)) !== null) {
    scripts.push({
      id: newScriptId(),
      name: readElement(m[1], 'Name'),
      code: readElement(m[1], 'Code').replace(/\r\n/g, '\n'),
    });
  }

  return { text, start, end, baseIndent, arrayOpenTag, scripts };
}

// Returns the decoded text of <Tag>…</Tag>, or '' for <Tag /> or a missing tag.
function readElement(xml, tag) {
  const paired = new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`).exec(xml);
  if (paired) return decodeXml(paired[1]);
  return '';
}

/**
 * Rebuild the whole preset file with a new set of scripts, leaving every byte
 * outside <Scripting> exactly as it was.
 */
export function serializePreset(preset, scripts) {
  // The first line carries no indent: the file's own whitespace before
  // <Scripting> sits outside the range we are replacing.
  const pad = preset.baseIndent ?? '  ';
  const lines = [];
  lines.push('<Scripting>');
  lines.push(`${pad}  ${preset.arrayOpenTag}`);
  for (const s of scripts) {
    lines.push(`${pad}    <Script>`);
    // vMix writes an empty script as a self-closing <Code />; match that so a
    // file with untouched empty scripts round-trips byte-for-byte.
    if (s.code === '') {
      lines.push(`${pad}      <Code />`);
    } else {
      // Script code sits flush against the opening tag with its own line
      // breaks — it is not re-indented to the XML nesting level.
      lines.push(`${pad}      <Code>${encodeXml(s.code).replace(/\n/g, CRLF)}</Code>`);
    }
    lines.push(`${pad}      <Name>${encodeXml(s.name)}</Name>`);
    lines.push(`${pad}    </Script>`);
  }
  lines.push(`${pad}  </ArrayOfScript>`);
  lines.push(`${pad}</Scripting>`);

  const region = lines.join(CRLF);
  return preset.text.slice(0, preset.start) + region + preset.text.slice(preset.end);
}

/**
 * The <Scripting>…</Scripting> slice on its own — what Mission Control stores.
 *
 * Everything the server needs to list, edit, clone and diff scripts lives in
 * here. The ~900KB of inputs, outputs and streaming settings around it stays on
 * the operator's disk, which is the point: stream keys live in that other part.
 */
export function extractScripting(text) {
  const preset = parsePreset(text);
  return {
    scripting: preset.text.slice(preset.start, preset.end),
    baseIndent: preset.baseIndent ?? '  ',
    arrayOpenTag: preset.arrayOpenTag,
    scripts: preset.scripts,
  };
}

/**
 * Rebuild a stored slice into a standalone <Scripting> block.
 *
 * The server has no full preset to splice into, so it synthesises the minimal
 * `preset` shape serializePreset needs. The browser then splices the result
 * into the operator's own file with spliceScripting().
 */
export function serializeScripting({ baseIndent = '  ', arrayOpenTag }, scripts) {
  const stub = { text: '', start: 0, end: 0, baseIndent, arrayOpenTag };
  return serializePreset(stub, scripts);
}

/**
 * Replace the <Scripting> block of a full preset with one the server produced.
 *
 * This is the download path for a library preset: the operator supplies their
 * original file, the server supplies the edited scripts, and every byte outside
 * <Scripting> comes from the operator's copy — so a stream key that was never
 * uploaded cannot be dropped, stale or overwritten on the way back.
 */
export function spliceScripting(fullText, scriptingBlock) {
  const preset = parsePreset(fullText);
  return preset.text.slice(0, preset.start) + scriptingBlock + preset.text.slice(preset.end);
}

/**
 * A name that doesn't collide with any existing script. vMix addresses scripts
 * by name (ScriptStart takes the name as its Value), so duplicates are a real
 * hazard rather than a cosmetic one — two scripts called "DDL VT List 3" mean
 * ScriptStart is a coin toss. The editor flags those and this keeps clones out
 * of that state.
 */
export function uniqueScriptName(base, scripts) {
  const taken = new Set(scripts.map(s => s.name));
  if (!taken.has(base)) return base;
  // "Foo copy", then "Foo copy 2", "Foo copy 3"…
  const root = /\bcopy(\s+\d+)?$/i.test(base) ? base.replace(/\s*\bcopy(\s+\d+)?$/i, '') : base;
  let candidate = `${root} copy`;
  let n = 2;
  while (taken.has(candidate)) candidate = `${root} copy ${n++}`;
  return candidate;
}

/** Script names used by more than one script — ScriptStart can't tell them apart. */
export function duplicateNames(scripts) {
  const counts = new Map();
  for (const s of scripts) counts.set(s.name, (counts.get(s.name) || 0) + 1);
  const dupes = new Set();
  for (const [name, n] of counts) if (n > 1) dupes.add(name);
  return dupes;
}
